import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Database, Plus, Server, FileSpreadsheet, Globe, CheckCircle2,
  AlertCircle, RefreshCw, Table2, Loader2, Trash2, Zap, Eye,
  Snowflake, HardDrive, X, Pencil, Upload, Save, ChevronRight, Wand2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { VersionHistoryButton } from '@/components/VersionHistory';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

const TYPE_ICONS = { snowflake: Snowflake, redshift: HardDrive, mongodb: Database, csv: FileSpreadsheet, excel: FileSpreadsheet, postgresql: Database, mysql: Database, sqlserver: Database };

// Product logo URLs (official SVG logos from CDN)
const PRODUCT_LOGOS = {
  postgresql: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg',
  mysql: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
  sqlserver: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/microsoftsqlserver/microsoftsqlserver-plain.svg',
  oracle: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/oracle/oracle-original.svg',
  snowflake: 'https://cdn.simpleicons.org/snowflake/29B5E8',
  bigquery: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg',
  redshift: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='8' fill='%238C4FFF'/%3E%3Cpath d='M32 14l14 8v20l-14 8-14-8V22z' fill='none' stroke='white' stroke-width='2'/%3E%3Cpath d='M32 14v16m0 0l14 8m-14-8l-14 8' fill='none' stroke='white' stroke-width='2'/%3E%3C/svg%3E",
  databricks: 'https://cdn.simpleicons.org/databricks/FF3621',
  trino: 'https://cdn.simpleicons.org/trino/DD00A1',
  mongodb: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg',
  csv: null,
  excel: null,
  parquet: null,
  json: null,
  rest_api: null,
  s3: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='8' fill='%23569A31'/%3E%3Cpath d='M32 12l-4 4h8l-4-4zm0 40l-4-4h8l-4 4zM20 24l-4 4v8l4 4m24-16l4 4v8l-4 4' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cellipse cx='32' cy='32' rx='8' ry='4' fill='none' stroke='white' stroke-width='2'/%3E%3C/svg%3E",
  azure_blob: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg',
  gcs: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg',
};

function ConnectorIcon({ type, size = 28 }) {
  const logo = PRODUCT_LOGOS[type];
  if (logo) {
    return <img src={logo} alt={type} width={size} height={size} className="object-contain" style={{ filter: 'drop-shadow(0 0 1px rgba(255,255,255,0.1))' }} />;
  }
  // Fallback to lucide icons for file types
  const Icon = TYPE_ICONS[type] || Database;
  const colorClass = TYPE_COLORS[type] || 'text-muted-foreground bg-muted/20 border-border/20';
  return (
    <div className={`w-${Math.round(size/4)} h-${Math.round(size/4)} rounded-lg flex items-center justify-center border ${colorClass}`}>
      <Icon className="w-4 h-4" />
    </div>
  );
}
const TYPE_COLORS = {
  snowflake: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
  redshift: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  mongodb: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  csv: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  postgresql: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
  mysql: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  sqlserver: 'text-red-400 bg-red-500/10 border-red-500/20',
  excel: 'text-green-400 bg-green-500/10 border-green-500/20',
  api: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
};

function WarehouseField({ field, value, onChange, allValues, sourceId }) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  const load = async () => {
    setLoading(true); setErr('');
    try {
      const r = await api.listWarehouses('snowflake', allValues, sourceId);
      if (r.success) {
        setList(r.warehouses || []);
        if (!(r.warehouses || []).length) setErr('No warehouses found for this user/role — enter it manually.');
      } else {
        setErr(r.message || 'Could not load warehouses');
      }
    } catch (e) { setErr(e.message || 'Could not load warehouses'); }
    finally { setLoading(false); }
  };
  const hasList = Array.isArray(list) && list.length > 0;
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{field.label}{field.required && <span className="text-destructive ml-0.5">*</span>}</Label>
      <div className="flex gap-2 items-center">
        {hasList ? (
          <Select value={value || ''} onValueChange={v => onChange('warehouse', v)}>
            <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-sm flex-1" data-testid="field-warehouse"><SelectValue placeholder="Select a warehouse" /></SelectTrigger>
            <SelectContent>
              {list.map(w => <SelectItem key={w} value={w} className="text-xs">{w}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <Input value={value || ''} onChange={e => onChange('warehouse', e.target.value)} placeholder={field.placeholder} className="rounded-lg bg-muted/50 border-border/50 text-sm flex-1" data-testid="field-warehouse" />
        )}
        <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading} className="shrink-0 rounded-lg text-xs" data-testid="load-warehouses-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Load'}
        </Button>
      </div>
      {err && <p className="text-[9px] text-amber-400">{err}</p>}
      {!err && field.help && <p className="text-[9px] text-muted-foreground/80">{field.help}</p>}
    </div>
  );
}

function ConnectorField({ field, value, onChange, allValues }) {
  // Conditional visibility
  if (field.show_when) {
    const [condKey, condVal] = Object.entries(field.show_when)[0];
    if (allValues[condKey] !== condVal) return null;
  }

  // Snowflake warehouse — picker with a "Load" button (falls back to text).
  if (field.name === 'warehouse') {
    return <WarehouseField field={field} value={value} onChange={onChange} allValues={allValues} />;
  }

  if (field.type === 'password') {
    return (
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{field.label}{field.required && <span className="text-destructive ml-0.5">*</span>}</Label>
        <Input type="password" value={value || ''} onChange={e => onChange(field.name, e.target.value)} placeholder={field.placeholder} className="rounded-lg bg-muted/50 border-border/50 text-sm" data-testid={`field-${field.name}`} />
        {field.help && <p className="text-[9px] text-muted-foreground/80">{field.help}</p>}
      </div>
    );
  }
  if (field.type === 'textarea') {
    const isKey = field.name?.includes('private_key') || field.name?.includes('key');
    const keyWarning = isKey && value && !value.includes('-----BEGIN') && value.length > 10;
    return (
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{field.label}{field.required && <span className="text-destructive ml-0.5">*</span>}</Label>
        <textarea value={value || ''} onChange={e => onChange(field.name, e.target.value)} placeholder={field.placeholder} className="w-full rounded-lg bg-muted/50 border border-border/50 text-xs p-2 text-foreground min-h-[80px] font-mono" data-testid={`field-${field.name}`} />
        {keyWarning && <p className="text-[9px] text-amber-400 bg-amber-500/10 px-2 py-1 rounded">Key should be in PEM format starting with -----BEGIN PRIVATE KEY----- or -----BEGIN RSA PRIVATE KEY-----. The current value does not appear to be in PEM format.</p>}
        {field.help && <p className="text-[9px] text-muted-foreground/80">{field.help}</p>}
      </div>
    );
  }
  if (field.type === 'select') {
    return (
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{field.label}</Label>
        <Select value={value || field.default || field.options?.[0]} onValueChange={v => onChange(field.name, v)}>
          <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-sm" data-testid={`field-${field.name}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {field.options?.map(opt => <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>)}
          </SelectContent>
        </Select>
        {field.help && <p className="text-[9px] text-muted-foreground/80">{field.help}</p>}
      </div>
    );
  }
  if (field.type === 'boolean') {
    return (
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">{field.label}</Label>
        <Switch checked={value !== undefined ? value : field.default} onCheckedChange={v => onChange(field.name, v)} data-testid={`field-${field.name}`} />
      </div>
    );
  }
  if (field.type === 'number') {
    return (
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{field.label}{field.required && <span className="text-destructive ml-0.5">*</span>}</Label>
        <Input type="number" value={value || field.default || ''} onChange={e => onChange(field.name, e.target.value)} placeholder={field.placeholder} className="rounded-lg bg-muted/50 border-border/50 text-sm" data-testid={`field-${field.name}`} />
      </div>
    );
  }
  if (field.type === 'file') {
    return (
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{field.label}{field.required && <span className="text-destructive ml-0.5">*</span>}</Label>
        <input
          type="file"
          accept={field.accept || '*'}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (file.size > (field.max_size_mb || 100) * 1024 * 1024) {
              toast.error(`File exceeds ${field.max_size_mb || 100}MB limit`);
              return;
            }
            if (field.accept?.includes('.xlsx') || field.accept?.includes('.xls')) {
              // Read as base64 for Excel
              const reader = new FileReader();
              reader.onload = () => {
                const base64 = reader.result.split(',')[1];
                onChange('file_content_base64', base64);
                onChange('_file_name', file.name);
              };
              reader.readAsDataURL(file);
            } else {
              // Read as text for CSV
              const reader = new FileReader();
              reader.onload = () => {
                onChange('file_content', reader.result);
                onChange('_file_name', file.name);
              };
              reader.readAsText(file);
            }
          }}
          className="w-full text-xs text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:bg-primary/20 file:text-primary file:cursor-pointer hover:file:bg-primary/30"
          data-testid={`field-${field.name}`}
        />
        {value && <p className="text-[9px] text-emerald-400">File loaded</p>}
      </div>
    );
  }
  // Default: text input
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{field.label}{field.required && <span className="text-destructive ml-0.5">*</span>}</Label>
      <Input value={value || ''} onChange={e => onChange(field.name, e.target.value)} placeholder={field.placeholder} className="rounded-lg bg-muted/50 border-border/50 text-sm" data-testid={`field-${field.name}`} />
      {field.help && <p className="text-[9px] text-muted-foreground/80">{field.help}</p>}
    </div>
  );
}

export default function DataSourcesPage() {
  const [sources, setSources] = useState([]);
  const [expandedSources, setExpandedSources] = useState(() => new Set());
  const [buildingModel, setBuildingModel] = useState(null);
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [formValues, setFormValues] = useState({});
  const [sourceName, setSourceName] = useState('');
  const [sourceDesc, setSourceDesc] = useState('');
  const [testing, setTesting] = useState(null); // source_id being tested
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [inlineTesting, setInlineTesting] = useState(false);
  const [inlineTestResult, setInlineTestResult] = useState(null);
  // Edit state
  const [editOpen, setEditOpen] = useState(false);
  const [editSource, setEditSource] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editConfig, setEditConfig] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [reuploadContent, setReuploadContent] = useState(null);
  const [reuploading, setReuploading] = useState(false);
  const [editFieldTypes, setEditFieldTypes] = useState({}); // {col_name: type}
  const [editFields, setEditFields] = useState([]); // [{name, data_type}]
  const [editFieldsLoading, setEditFieldsLoading] = useState(false);
  const [savingFieldTypes, setSavingFieldTypes] = useState(false);
  // File preview state
  const [filePreview, setFilePreview] = useState(null); // {columns, sample_rows, total_rows}
  const [fileColumnTypes, setFileColumnTypes] = useState({}); // {col_name: type}
  const [filePreviewing, setFilePreviewing] = useState(false);
  const [fileIngesting, setFileIngesting] = useState(false);
  // Multi-sheet state for Excel
  const [allSheetsData, setAllSheetsData] = useState(null); // [{name, columns, sample_rows, total_rows}]
  const [selectedSheets, setSelectedSheets] = useState(new Set()); // Set of selected sheet names
  const [activeSheetTab, setActiveSheetTab] = useState(''); // Currently viewed sheet tab
  const [perSheetColumnTypes, setPerSheetColumnTypes] = useState({}); // {sheetName: {col: type}}

  const dsIsDirty = (addOpen && (sourceName.trim().length > 0 || selectedType)) || (editOpen && editSource);
  const { confirmNavigation: dsConfirmNav, UnsavedDialog: DsUnsavedDialog } = useUnsavedChanges(dsIsDirty, 'You have unsaved data source changes. Discard and leave?');

  useEffect(() => {
    Promise.all([api.getDataSources(), api.getConnectorSchemas()])
      .then(([s, sc]) => { setSources(s); setSchemas(sc); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const setFieldValue = (name, value) => {
    setFormValues(prev => ({ ...prev, [name]: value }));
    setInlineTestResult(null);
    // Auto-preview when file content is set
    if (name === 'file_content' && value) {
      previewUploadedFile('csv', value, null);
    }
    if (name === 'file_content_base64' && value) {
      previewExcelAllSheetsFromBase64(value);
    }
  };

  const previewExcelAllSheetsFromBase64 = async (base64Content) => {
    setFilePreviewing(true);
    try {
      const payload = { content_base64: base64Content };
      const jsonStr = JSON.stringify(payload);
      const CHUNK_SIZE = 100000;
      const uploadId = 'preview_all_' + Date.now();
      const totalChunks = Math.ceil(jsonStr.length / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const chunk = jsonStr.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        await api.uploadChunk(uploadId, i, totalChunks, chunk);
      }
      const result = await api.finalizeChunkedPreviewAllSheets(uploadId);
      const sheets = result.sheets || [];
      setAllSheetsData(sheets);

      // Auto-select all non-empty sheets
      const nonEmpty = sheets.filter(s => s.total_rows > 0).map(s => s.name);
      setSelectedSheets(new Set(nonEmpty.length > 0 ? nonEmpty : sheets.map(s => s.name)));
      setActiveSheetTab(nonEmpty[0] || sheets[0]?.name || '');

      // Build per-sheet column types from inferred types
      const types = {};
      sheets.forEach(sheet => {
        types[sheet.name] = {};
        (sheet.columns || []).forEach(c => { types[sheet.name][c.name] = c.inferred_type; });
      });
      setPerSheetColumnTypes(types);

      // Set single-sheet preview for backwards compat (first sheet)
      if (sheets.length === 1) {
        setFilePreview({ columns: sheets[0].columns, sample_rows: sheets[0].sample_rows, total_rows: sheets[0].total_rows, sheets: sheets.map(s => s.name) });
        const ft = {};
        (sheets[0].columns || []).forEach(c => { ft[c.name] = c.inferred_type; });
        setFileColumnTypes(ft);
      } else {
        setFilePreview({ columns: [], sample_rows: [], total_rows: 0, sheets: sheets.map(s => s.name) });
      }
    } catch (err) {
      toast.error('Preview failed: ' + err.message);
      setAllSheetsData(null);
      setFilePreview(null);
    } finally {
      setFilePreviewing(false);
    }
  };

  const previewUploadedFile = async (fileType, csvContent, excelBase64) => {
    setFilePreviewing(true);
    try {
      const payload = { file_type: fileType };
      if (fileType === 'csv') payload.content = csvContent;
      else payload.content_base64 = excelBase64;

      let result;
      if (fileType === 'excel') {
        const jsonStr = JSON.stringify(payload);
        const CHUNK_SIZE = 100000;
        const uploadId = 'preview_' + Date.now();
        const totalChunks = Math.ceil(jsonStr.length / CHUNK_SIZE);
        for (let i = 0; i < totalChunks; i++) {
          const chunk = jsonStr.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          await api.uploadChunk(uploadId, i, totalChunks, chunk);
        }
        result = await api.finalizeChunkedPreview(uploadId);
      } else {
        result = await api.previewFile(payload);
      }
      setFilePreview(result);
      const types = {};
      (result.columns || []).forEach(function(c) { types[c.name] = c.inferred_type; });
      setFileColumnTypes(types);
    } catch (err) { toast.error('Preview failed: ' + err.message); setFilePreview(null); }
    finally { setFilePreviewing(false); }
  };

  const handleIngestFile = async () => {
    if (!sourceName.trim()) { toast.error('Source name required'); return; }
    setFileIngesting(true);
    try {
      if (selectedType === 'excel' && allSheetsData && allSheetsData.length > 1) {
        // Multi-sheet Excel → ONE data source containing several tables (joinable in a model)
        const sheetsToImport = allSheetsData.filter(s => selectedSheets.has(s.name));
        if (sheetsToImport.length === 0) { toast.error('Select at least one sheet'); setFileIngesting(false); return; }

        const payload = {
          name: sourceName,
          description: sourceDesc || `Excel workbook: ${sheetsToImport.length} tables`,
          content_base64: formValues.file_content_base64,
          sheets: sheetsToImport.map(sheet => ({
            sheet_name: sheet.name,
            column_types: perSheetColumnTypes[sheet.name] || {},
          })),
        };
        const jsonStr = JSON.stringify(payload);
        const CHUNK_SIZE = 100000;
        const uploadId = 'up_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
        const totalChunks = Math.ceil(jsonStr.length / CHUNK_SIZE);
        for (let i = 0; i < totalChunks; i++) {
          const chunk = jsonStr.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          await api.uploadChunk(uploadId, i, totalChunks, chunk);
        }
        const result = await api.finalizeChunkedIngestMulti(uploadId);
        try { setSources(await api.getDataSources()); }
        catch { setSources(prev => [{ source_id: result.source_id, name: sourceName, source_type: selectedType, description: payload.description, status: 'connected', tables_count: result.tables_count }, ...prev]); }
        toast.success(`Imported ${result.tables_count} tables into "${sourceName}"`);
      } else {
        // Single sheet Excel or CSV
        const payload = { file_type: selectedType, name: sourceName, description: sourceDesc, column_types: fileColumnTypes, delimiter: formValues.delimiter || ',' };
        if (selectedType === 'csv') payload.content = formValues.file_content;
        else {
          payload.content_base64 = formValues.file_content_base64;
          if (allSheetsData?.length === 1) {
            payload.sheet_name = allSheetsData[0].name;
            payload.column_types = perSheetColumnTypes[allSheetsData[0].name] || fileColumnTypes;
          }
        }

        const jsonStr = JSON.stringify(payload);
        const CHUNK_SIZE = 100000;
        const uploadId = 'up_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
        const totalChunks = Math.ceil(jsonStr.length / CHUNK_SIZE);
        for (let i = 0; i < totalChunks; i++) {
          const chunk = jsonStr.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          await api.uploadChunk(uploadId, i, totalChunks, chunk);
        }
        const result = await api.finalizeChunkedIngest(uploadId);
        setSources(prev => [{ source_id: result.source_id, name: sourceName, source_type: selectedType, description: sourceDesc, status: 'connected', tables_count: 1 }, ...prev]);
        toast.success(`${result.rows_inserted} rows imported`);
      }
      setAddOpen(false);
      resetForm();
    } catch (err) { toast.error(err.message); }
    finally { setFileIngesting(false); }
  };

  const handleTestInline = async () => {
    setInlineTesting(true);
    setInlineTestResult(null);
    try {
      const result = await api.testInlineConnection(selectedType, formValues);
      setInlineTestResult(result);
    } catch (err) {
      setInlineTestResult({ success: false, message: err.message });
    } finally {
      setInlineTesting(false);
    }
  };

  const handleSave = async () => {
    if (!sourceName.trim()) { toast.error('Source name required'); return; }
    setSaving(true);
    try {
      const result = await api.createRealConnection({
        source_type: selectedType,
        name: sourceName,
        description: sourceDesc,
        connection_config: formValues,
      });
      setSources(prev => [result, ...prev]);
      setAddOpen(false);
      resetForm();
      toast.success('Data source created');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleTestExisting = async (sourceId) => {
    setTesting(sourceId);
    setTestResult(null);
    try {
      const result = await api.testDataSource(sourceId);
      setTestResult({ sourceId, ...result });
      setSources(prev => prev.map(s => s.source_id === sourceId ? { ...s, status: result.success ? 'connected' : 'error', ...(typeof result.tables_count === 'number' ? { tables_count: result.tables_count } : {}) } : s));
    } catch (err) {
      setTestResult({ sourceId, success: false, message: err.message });
    } finally {
      setTesting(null);
    }
  };

  const handleDelete = async (sourceId) => {
    try {
      await api.deleteDataSource(sourceId);
      setSources(prev => prev.filter(s => s.source_id !== sourceId));
      toast.success('Data source deleted');
    } catch (err) { toast.error(err.message); }
  };

  const toggleExpand = (sourceId) => setExpandedSources(prev => {
    const next = new Set(prev);
    if (next.has(sourceId)) next.delete(sourceId); else next.add(sourceId);
    return next;
  });

  const handleBuildModel = async (source) => {
    setBuildingModel(source.source_id);
    try {
      const r = await api.buildModelFromSource(source.source_id);
      toast.success(`Starter model created — ${r.entities_created} tables, ${r.joins_created} join${r.joins_created === 1 ? '' : 's'}`);
      navigate(`/semantic-models?model=${r.model_id}`);
    } catch (err) { toast.error(err.message); }
    setBuildingModel(null);
  };

  const openEdit = async (source) => {
    try {
      const full = await api.getDataSource(source.source_id);
      setEditSource(full);
      setEditName(full.name);
      setEditDesc(full.description || '');
      setEditConfig(full.connection_config || {});
      setReuploadContent(null);
      setEditOpen(true);
      // Load field schema for file-based sources
      if ((full.source_type === 'csv' || full.source_type === 'excel' || full.source_type === 'prepared') && full.collection_name) {
        setEditFieldsLoading(true);
        try {
          const schema = await api.introspectCollection(full.collection_name);
          const fields = (schema.fields || []).map(f => ({ name: f.name, data_type: f.data_type }));
          setEditFields(fields);
          const types = {};
          const storedTypes = (full.connection_config || {}).column_types || {};
          fields.forEach(f => { types[f.name] = storedTypes[f.name] || f.data_type || 'string'; });
          setEditFieldTypes(types);
        } catch { setEditFields([]); setEditFieldTypes({}); }
        finally { setEditFieldsLoading(false); }
      } else {
        setEditFields([]);
        setEditFieldTypes({});
      }
    } catch (err) { toast.error(err.message); }
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      const updated = await api.updateDataSource(editSource.source_id, {
        name: editName,
        description: editDesc,
        connection_config: editConfig,
      });
      setSources(prev => prev.map(s => s.source_id === editSource.source_id ? { ...s, ...updated } : s));
      setEditOpen(false);
      toast.success('Data source updated');
    } catch (err) { toast.error(err.message); }
    finally { setEditSaving(false); }
  };

  const handleReupload = async () => {
    if (!editSource || !reuploadContent) return;
    setReuploading(true);
    try {
      const payload = editSource.source_type === 'excel'
        ? { file_content_base64: reuploadContent }
        : { file_content: reuploadContent };
      const result = await api.reuploadFileSource(editSource.source_id, payload);
      toast.success(result.message);
      // Refresh sources
      const refreshed = await api.getDataSources();
      setSources(refreshed);
      setReuploadContent(null);
    } catch (err) { toast.error(err.message); }
    finally { setReuploading(false); }
  };

  const handleSaveFieldTypes = async () => {
    if (!editSource) return;
    setSavingFieldTypes(true);
    try {
      const result = await api.updateFieldTypes(editSource.source_id, editFieldTypes);
      toast.success(result.message);
    } catch (err) { toast.error(err.message); }
    finally { setSavingFieldTypes(false); }
  };

  const resetForm = () => {
    setSelectedType('');
    setFormValues({});
    setSourceName('');
    setSourceDesc('');
    setInlineTestResult(null);
    setFilePreview(null);
    setFileColumnTypes({});
    setAllSheetsData(null);
    setSelectedSheets(new Set());
    setActiveSheetTab('');
    setPerSheetColumnTypes({});
  };

  const selectedSchema = schemas.find(s => s.type === selectedType);

  return (
    <div className="p-6 space-y-6" data-testid="data-sources-page">
      <div className="flex items-center justify-between animate-slide-up">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Data Sources</h1>
          <p className="text-sm text-muted-foreground mt-1">Connect databases, warehouses, and upload files</p>
        </div>
        <Button onClick={() => { resetForm(); setAddOpen(true); }} data-testid="add-data-source-btn" className="rounded-xl bg-primary text-primary-foreground shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all active:scale-95">
          <Plus className="w-4 h-4 mr-2" /> Add Source
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-slide-up" style={{ animationDelay: '100ms' }}>
        <div className="glass-card rounded-2xl p-4 flex items-center gap-3">
          <div className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-lg font-heading font-bold text-foreground">{sources.filter(s => s.status === 'connected').length}</p>
            <p className="text-xs text-muted-foreground">Connected</p>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-4 flex items-center gap-3">
          <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <Table2 className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-lg font-heading font-bold text-foreground">{sources.reduce((acc, s) => acc + (s.tables_count || 0), 0)}</p>
            <p className="text-xs text-muted-foreground">Total Tables</p>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-4 flex items-center gap-3">
          <div className="p-2 rounded-xl bg-purple-500/10 border border-purple-500/20">
            <Database className="w-5 h-5 text-purple-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-lg font-heading font-bold text-foreground">{sources.length}</p>
            <p className="text-xs text-muted-foreground">Total Sources</p>
          </div>
        </div>
      </div>

      {/* Sources List */}
      <div className="space-y-3 stagger-children" data-testid="sources-list">
        {sources.map(source => {
          const colorClass = TYPE_COLORS[source.source_type] || TYPE_COLORS.postgresql;
          const isTestingThis = testing === source.source_id;
          const thisTestResult = testResult?.sourceId === source.source_id ? testResult : null;

          return (
            <div key={source.source_id} className="glass-card rounded-2xl p-5 hover:border-primary/20 transition-all group" data-testid={`source-${source.source_id}`}>
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl border ${colorClass} group-hover:scale-105 transition-transform`}>
                  <ConnectorIcon type={source.source_type} size={28} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-heading font-semibold text-foreground truncate">{source.name}</h3>
                    <Badge className={`text-[10px] ${source.status === 'connected' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : source.status === 'error' ? 'bg-red-500/15 text-red-400 border-red-500/20' : 'bg-amber-500/15 text-amber-400 border-amber-500/20'}`}>
                      {source.status === 'connected' && <CheckCircle2 className="w-2.5 h-2.5 mr-1" />}
                      {source.status === 'error' && <AlertCircle className="w-2.5 h-2.5 mr-1" />}
                      {source.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1">{source.description}</p>
                  {/* Test result */}
                  {thisTestResult && (
                    <div className={`mt-2 p-2 rounded-lg text-[10px] ${thisTestResult.success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                      {thisTestResult.message}
                      {thisTestResult.details?.version && <span className="ml-2 text-muted-foreground">({thisTestResult.details.version})</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right mr-2">
                    <p className="text-sm font-heading font-bold text-foreground" data-testid={`source-tables-count-${source.source_id}`}>{source.tables_count}</p>
                    <p className="text-[10px] text-muted-foreground">tables</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] border-border/50 text-muted-foreground">{source.source_type}</Badge>
                  {(source.tables_count || 0) >= 1 && (
                    <Button variant="outline" size="sm" onClick={() => handleBuildModel(source)} disabled={buildingModel === source.source_id} className="rounded-lg text-xs gap-1" data-testid={`build-model-${source.source_id}`}>
                      {buildingModel === source.source_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Build model
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handleTestExisting(source.source_id)} disabled={isTestingThis} className="rounded-lg text-xs gap-1" data-testid={`test-source-${source.source_id}`}>
                    {isTestingThis ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Test
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEdit(source)} className="rounded-lg text-xs gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-source-${source.source_id}`}>
                    <Pencil className="w-3 h-3" /> Edit
                  </Button>
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity"><VersionHistoryButton resourceType="data_source" resourceId={source.source_id} resourceName={source.name} /></span>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(source.source_id)} className="rounded-lg w-8 h-8 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-source-${source.source_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Table Explorer — expandable list of tables inside a multi-table source */}
              {Array.isArray(source.tables) && source.tables.length > 1 && (
                <div className="mt-3 pt-3 border-t border-border/30">
                  <button onClick={() => toggleExpand(source.source_id)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid={`toggle-tables-${source.source_id}`}>
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expandedSources.has(source.source_id) ? 'rotate-90' : ''}`} />
                    {source.tables.length} tables
                  </button>
                  {expandedSources.has(source.source_id) && (
                    <div className="mt-2 grid gap-1.5 sm:grid-cols-2" data-testid={`tables-list-${source.source_id}`}>
                      {source.tables.map((t, ti) => (
                        <div key={ti} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-muted/20 border border-border/20" data-testid={`table-item-${source.source_id}-${ti}`}>
                          <Table2 className="w-3.5 h-3.5 text-primary/70 flex-shrink-0" />
                          <span className="text-xs font-medium text-foreground truncate">{t.display_name || t.sheet_name || t.table_name}</span>
                          <span className="text-[10px] text-muted-foreground ml-auto flex-shrink-0">{(t.columns?.length ?? 0)} cols · {t.row_count ?? 0} rows</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Data Source Dialog */}
      <Dialog open={addOpen} onOpenChange={v => { if (!v) resetForm(); setAddOpen(v); }}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">
              {selectedType ? `Configure ${selectedSchema?.display_name || selectedType}` : 'Add Data Source'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {!selectedType ? (
              /* Step 1: Select connector type */
              <div className="grid grid-cols-2 gap-3 mt-2" data-testid="connector-type-grid">
                {schemas.map(schema => {
                  const color = TYPE_COLORS[schema.type] || '';
                  return (
                    <button key={schema.type} onClick={() => { setSelectedType(schema.type); const defaults = {}; (schema.fields || []).forEach(f => { if (f.default) defaults[f.name] = f.default; }); setFormValues(prev => ({ ...prev, ...defaults })); }} className={`flex items-center gap-3 p-4 rounded-xl border border-border/30 hover:border-primary/30 hover:bg-primary/5 transition-all active:scale-[0.98] text-left`} data-testid={`select-${schema.type}`}>
                      <div className={`p-2 rounded-lg border ${color}`}>
                        <ConnectorIcon type={schema.type} size={24} />
                      </div>
                      <div>
                        <p className="text-sm font-heading font-semibold text-foreground">{schema.display_name}</p>
                        <p className="text-[10px] text-muted-foreground">{schema.fields?.length || 0} config fields</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              /* Step 2: Configure connection */
              <div className="space-y-4 mt-2">
                <div className="flex items-center gap-2">
                  <button onClick={() => { setSelectedType(''); setFormValues({}); setInlineTestResult(null); }} className="text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-muted-foreground">Back to connector selection</span>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Source Name<span className="text-destructive ml-0.5">*</span></Label>
                  <Input data-testid="source-name-input" value={sourceName} onChange={e => setSourceName(e.target.value)} placeholder={`My ${selectedSchema?.display_name || ''} Connection`} className="rounded-lg bg-muted/50 border-border/50 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <Input value={sourceDesc} onChange={e => setSourceDesc(e.target.value)} placeholder="Optional description" className="rounded-lg bg-muted/50 border-border/50 text-xs" />
                </div>

                <Separator className="bg-border/30" />

                {/* Dynamic credential fields */}
                {selectedSchema?.fields?.map(field => (
                  <ConnectorField key={field.name} field={field} value={formValues[field.name]} onChange={setFieldValue} allValues={formValues} />
                ))}

                {/* Inline test result */}
                {inlineTestResult && (
                  <div className={`p-3 rounded-xl ${inlineTestResult.success ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`} data-testid="inline-test-result">
                    <div className="flex items-center gap-2">
                      {inlineTestResult.success ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-red-400" />}
                      <p className={`text-xs font-medium ${inlineTestResult.success ? 'text-emerald-400' : 'text-red-400'}`}>{inlineTestResult.message}</p>
                    </div>
                    {inlineTestResult.details && Object.keys(inlineTestResult.details).length > 0 && (
                      <div className="mt-2 text-[10px] text-muted-foreground font-mono">
                        {Object.entries(inlineTestResult.details).filter(([k]) => k !== 'error' && k !== 'driver_missing').map(([k, v]) => (
                          <div key={k}>{k}: {Array.isArray(v) ? v.join(', ') : String(v)}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* File Preview & Type Configuration */}
                {selectedType === 'csv' && filePreview && (
                  <div className="space-y-2">
                    <Separator className="bg-border/30" />
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Field Configuration</p>
                      <span className="text-[9px] text-muted-foreground">{filePreview.total_rows} rows detected</span>
                    </div>
                    <div className="rounded-lg border border-border/30 overflow-auto max-h-52">
                      <table className="w-full text-[10px]">
                        <thead>
                          <tr className="border-b border-border/30 bg-muted/20">
                            <th className="px-2 py-1.5 text-left text-muted-foreground font-semibold">Field</th>
                            <th className="px-2 py-1.5 text-left text-muted-foreground font-semibold">Data Type</th>
                            <th className="px-2 py-1.5 text-left text-muted-foreground font-semibold">Sample</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filePreview.columns.map(function(col) { return (
                            <tr key={col.name} className="border-b border-border/10 hover:bg-muted/10">
                              <td className="px-2 py-1.5 font-mono text-foreground">{col.name}</td>
                              <td className="px-2 py-1">
                                <Select value={fileColumnTypes[col.name] || col.inferred_type} onValueChange={function(v) { setFileColumnTypes(function(prev) { var n = Object.assign({}, prev); n[col.name] = v; return n; }); }}>
                                  <SelectTrigger className="h-6 text-[9px] rounded bg-muted/30 border-border/30 w-24"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="string">String</SelectItem>
                                    <SelectItem value="integer">Integer</SelectItem>
                                    <SelectItem value="float">Float</SelectItem>
                                    <SelectItem value="boolean">Boolean</SelectItem>
                                    <SelectItem value="date">Date</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-2 py-1.5 text-muted-foreground/80 truncate max-w-[120px]">{(col.sample_values || []).filter(Boolean).slice(0, 3).join(', ')}</td>
                            </tr>
                          ); })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Excel Multi-Sheet Preview */}
                {selectedType === 'excel' && allSheetsData && allSheetsData.length > 0 && (
                  <div className="space-y-3" data-testid="excel-sheets-preview">
                    <Separator className="bg-border/30" />
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Sheets ({allSheetsData.length})</p>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => { setSelectedSheets(new Set(allSheetsData.map(s => s.name))); }}
                          className="text-[9px] text-primary hover:underline"
                          data-testid="select-all-sheets"
                        >Select All</button>
                        <span className="text-[9px] text-muted-foreground/30">|</span>
                        <button
                          onClick={() => { setSelectedSheets(new Set()); }}
                          className="text-[9px] text-muted-foreground hover:underline"
                          data-testid="deselect-all-sheets"
                        >None</button>
                      </div>
                    </div>

                    {/* Worksheet checkboxes — each selected sheet imports as its own table */}
                    <div className="space-y-1" data-testid="sheet-checkbox-list">
                      {allSheetsData.map(sheet => {
                        const isSelected = selectedSheets.has(sheet.name);
                        const isActive = activeSheetTab === sheet.name;
                        const toggle = () => setSelectedSheets(prev => {
                          const next = new Set(prev);
                          if (next.has(sheet.name)) next.delete(sheet.name); else next.add(sheet.name);
                          return next;
                        });
                        return (
                          <div
                            key={sheet.name}
                            className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg border transition-all ${
                              isActive ? 'border-primary/50 bg-primary/10' : 'border-border/30 hover:border-border/50'
                            }`}
                            data-testid={`sheet-row-${sheet.name}`}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={toggle}
                              disabled={sheet.total_rows === 0}
                              data-testid={`sheet-checkbox-${sheet.name}`}
                            />
                            <button
                              type="button"
                              onClick={() => setActiveSheetTab(sheet.name)}
                              className="flex-1 flex items-center justify-between text-left min-w-0"
                              data-testid={`sheet-view-${sheet.name}`}
                            >
                              <span className={`text-xs font-medium truncate ${isSelected ? 'text-foreground' : 'text-muted-foreground'}`}>{sheet.name}</span>
                              <span className="text-[9px] text-muted-foreground/80 ml-2 flex-shrink-0">
                                {sheet.total_rows === 0 ? 'empty' : `${sheet.total_rows}r / ${sheet.columns.length}c`}
                                {isActive ? ' · viewing' : ''}
                              </span>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[8px] text-muted-foreground/70">Tick a worksheet to import it. All selected worksheets are imported into this one data source as separate tables you can join in a model. Click a name to preview it below.</p>

                    {/* Active Sheet Detail */}
                    {activeSheetTab && (() => {
                      const sheet = allSheetsData.find(s => s.name === activeSheetTab);
                      if (!sheet) return null;
                      const sheetTypes = perSheetColumnTypes[sheet.name] || {};
                      return (
                        <div className="space-y-2 animate-slide-up" data-testid={`sheet-detail-${sheet.name}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileSpreadsheet className="w-3.5 h-3.5 text-green-400" />
                              <p className="text-xs font-heading font-semibold text-foreground">{sheet.name}</p>
                              {!selectedSheets.has(sheet.name) && (
                                <button onClick={() => setSelectedSheets(prev => new Set([...prev, sheet.name]))} className="text-[9px] text-primary hover:underline">+ Include</button>
                              )}
                            </div>
                            <span className="text-[9px] text-muted-foreground">{sheet.total_rows} rows, {sheet.columns.length} columns</span>
                          </div>
                          <div className="rounded-lg border border-border/30 overflow-auto max-h-48">
                            <table className="w-full text-[10px]">
                              <thead>
                                <tr className="border-b border-border/30 bg-muted/20 sticky top-0">
                                  <th className="px-2 py-1.5 text-left text-muted-foreground font-semibold">Field</th>
                                  <th className="px-2 py-1.5 text-left text-muted-foreground font-semibold">Data Type</th>
                                  <th className="px-2 py-1.5 text-left text-muted-foreground font-semibold">Sample</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sheet.columns.map(col => (
                                  <tr key={col.name} className="border-b border-border/10 hover:bg-muted/10">
                                    <td className="px-2 py-1.5 font-mono text-foreground">{col.name}</td>
                                    <td className="px-2 py-1">
                                      <Select
                                        value={sheetTypes[col.name] || col.inferred_type}
                                        onValueChange={v => {
                                          setPerSheetColumnTypes(prev => ({
                                            ...prev,
                                            [sheet.name]: { ...(prev[sheet.name] || {}), [col.name]: v }
                                          }));
                                        }}
                                      >
                                        <SelectTrigger className="h-6 text-[9px] rounded bg-muted/30 border-border/30 w-24"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="string">String</SelectItem>
                                          <SelectItem value="integer">Integer</SelectItem>
                                          <SelectItem value="float">Float</SelectItem>
                                          <SelectItem value="boolean">Boolean</SelectItem>
                                          <SelectItem value="date">Date</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </td>
                                    <td className="px-2 py-1.5 text-muted-foreground/80 truncate max-w-[120px]">{(col.sample_values || []).filter(Boolean).slice(0, 3).join(', ')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Sample Data Preview */}
                          {sheet.sample_rows.length > 0 && (
                            <details className="group">
                              <summary className="text-[9px] text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1">
                                <Eye className="w-3 h-3" /> Preview data ({Math.min(sheet.sample_rows.length, 5)} rows)
                              </summary>
                              <div className="mt-1.5 rounded-lg border border-border/20 overflow-auto max-h-32">
                                <table className="w-full text-[9px]">
                                  <thead>
                                    <tr className="border-b border-border/20 bg-muted/10">
                                      {sheet.columns.map(c => <th key={c.name} className="px-1.5 py-1 text-left text-muted-foreground font-medium whitespace-nowrap">{c.name}</th>)}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sheet.sample_rows.slice(0, 5).map((row, ri) => (
                                      <tr key={ri} className="border-b border-border/10">
                                        {sheet.columns.map(c => <td key={c.name} className="px-1.5 py-0.5 text-foreground/70 whitespace-nowrap max-w-[100px] truncate">{row[c.name] ?? ''}</td>)}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </details>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
                {(selectedType === 'csv' || selectedType === 'excel') && filePreviewing && (
                  <div className="flex items-center gap-2 py-4 justify-center"><Loader2 className="w-4 h-4 text-primary animate-spin" /><span className="text-xs text-muted-foreground">Analyzing file...</span></div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  {selectedType === 'csv' && filePreview ? (
                    <Button onClick={handleIngestFile} disabled={fileIngesting || !sourceName.trim()} className="flex-1 rounded-lg bg-primary text-primary-foreground text-xs active:scale-95" data-testid="ingest-file-btn">
                      {fileIngesting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                      Import {filePreview.total_rows} Rows
                    </Button>
                  ) : selectedType === 'excel' && allSheetsData ? (
                    <Button onClick={handleIngestFile} disabled={fileIngesting || !sourceName.trim() || selectedSheets.size === 0} className="flex-1 rounded-lg bg-primary text-primary-foreground text-xs active:scale-95" data-testid="ingest-file-btn">
                      {fileIngesting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                      Import {selectedSheets.size} Sheet{selectedSheets.size !== 1 ? 's' : ''} ({allSheetsData.filter(s => selectedSheets.has(s.name)).reduce((acc, s) => acc + s.total_rows, 0)} rows)
                    </Button>
                  ) : (
                    <React.Fragment>
                      <Button variant="outline" onClick={handleTestInline} disabled={inlineTesting} className="flex-1 rounded-lg text-xs active:scale-95" data-testid="test-connection-btn">
                        {inlineTesting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
                        Test Connection
                      </Button>
                      <Button onClick={handleSave} disabled={saving || !sourceName.trim()} className="flex-1 rounded-lg bg-primary text-primary-foreground text-xs active:scale-95" data-testid="save-source-btn">
                        {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                        Save Source
                      </Button>
                    </React.Fragment>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Data Source Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Edit Data Source</DialogTitle>
          </DialogHeader>
          {editSource && (
            <div className="flex-1 overflow-y-auto -mx-6 px-6">
              <div className="space-y-4 mt-2">
                {/* Name & Description */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Source Name</Label>
                  <Input data-testid="edit-source-name" value={editName} onChange={e => setEditName(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 text-xs" />
                </div>

                <Separator className="bg-border/30" />

                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-[10px]">{editSource.source_type}</Badge>
                  <span className="text-[10px] text-muted-foreground">Connection Configuration</span>
                </div>

                {/* Dynamic config fields from schema */}
                {(() => {
                  const schema = schemas.find(s => s.type === editSource.source_type);
                  if (!schema) return <p className="text-xs text-muted-foreground">No configurable fields for this connector type.</p>;
                  return schema.fields.filter(f => f.type !== 'file').map(field => {
                    // Check conditional visibility
                    if (field.show_when) {
                      const [ck, cv] = Object.entries(field.show_when)[0];
                      if (editConfig[ck] !== cv) return null;
                    }
                    const val = editConfig[field.name];
                    const isSensitive = ['password', 'private_key', 'private_key_passphrase', 'connection_string'].includes(field.name);
                    if (field.name === 'warehouse') {
                      return (
                        <WarehouseField
                          key={field.name}
                          field={field}
                          value={val}
                          onChange={(n, v) => setEditConfig(c => ({ ...c, [n]: v }))}
                          allValues={editConfig}
                          sourceId={editSource?.source_id}
                        />
                      );
                    }
                    return (
                      <div key={field.name} className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{field.label}</Label>
                        {field.type === 'select' ? (
                          <Select value={val || field.default || field.options?.[0]} onValueChange={v => setEditConfig(c => ({ ...c, [field.name]: v }))}>
                            <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {field.options?.map(o => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : field.type === 'boolean' ? (
                          <Switch checked={val !== undefined ? val : field.default} onCheckedChange={v => setEditConfig(c => ({ ...c, [field.name]: v }))} />
                        ) : field.type === 'textarea' ? (
                          <textarea value={val || ''} onChange={e => setEditConfig(c => ({ ...c, [field.name]: e.target.value }))} placeholder={isSensitive ? 'Leave blank to keep current' : field.placeholder} className="w-full rounded-lg bg-muted/50 border border-border/50 text-xs p-2 text-foreground min-h-[60px] font-mono" />
                        ) : (
                          <Input
                            type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
                            value={val || ''}
                            onChange={e => setEditConfig(c => ({ ...c, [field.name]: e.target.value }))}
                            placeholder={isSensitive ? 'Leave blank to keep current' : field.placeholder}
                            className="rounded-lg bg-muted/50 border-border/50 text-sm"
                            data-testid={`edit-field-${field.name}`}
                          />
                        )}
                        {isSensitive && val === '********' && <p className="text-[9px] text-muted-foreground/70">Current value is encrypted. Leave as-is or enter a new value.</p>}
                      </div>
                    );
                  });
                })()}

                {/* File Re-upload for CSV/Excel */}
                {(editSource.source_type === 'csv' || editSource.source_type === 'excel' || editSource.source_type === 'prepared') && editFields.length > 0 && (
                  <>
                    <Separator className="bg-border/30" />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Table2 className="w-4 h-4 text-primary" />
                          <Label className="text-xs font-heading font-semibold text-foreground">Field Types</Label>
                        </div>
                        <span className="text-[9px] text-muted-foreground">{editFields.length} fields</span>
                      </div>
                      <div className="rounded-lg border border-border/30 overflow-auto max-h-52">
                        <table className="w-full text-[10px]">
                          <thead>
                            <tr className="border-b border-border/30 bg-muted/20 sticky top-0">
                              <th className="px-2 py-1.5 text-left text-muted-foreground font-semibold">Field</th>
                              <th className="px-2 py-1.5 text-left text-muted-foreground font-semibold">Data Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {editFields.map(f => (
                              <tr key={f.name} className="border-b border-border/10 hover:bg-muted/10">
                                <td className="px-2 py-1.5 font-mono text-foreground">{f.name}</td>
                                <td className="px-2 py-1">
                                  <Select value={editFieldTypes[f.name] || f.data_type || 'string'} onValueChange={v => setEditFieldTypes(prev => ({ ...prev, [f.name]: v }))}>
                                    <SelectTrigger className="h-6 text-[9px] rounded bg-muted/30 border-border/30 w-24"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="string">String</SelectItem>
                                      <SelectItem value="integer">Integer</SelectItem>
                                      <SelectItem value="float">Float</SelectItem>
                                      <SelectItem value="boolean">Boolean</SelectItem>
                                      <SelectItem value="date">Date</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <Button variant="outline" onClick={handleSaveFieldTypes} disabled={savingFieldTypes} className="w-full rounded-lg text-xs active:scale-95" data-testid="save-field-types-btn">
                        {savingFieldTypes ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                        Apply Field Types
                      </Button>
                    </div>
                  </>
                )}
                {editFieldsLoading && (
                  <div className="flex items-center gap-2 py-2 justify-center"><Loader2 className="w-4 h-4 text-primary animate-spin" /><span className="text-xs text-muted-foreground">Loading fields...</span></div>
                )}

                {/* File Re-upload for CSV/Excel - upload new file */}
                {(editSource.source_type === 'csv' || editSource.source_type === 'excel') && (
                  <>
                    <Separator className="bg-border/30" />
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Upload className="w-4 h-4 text-primary" />
                        <Label className="text-xs font-heading font-semibold text-foreground">Re-upload File</Label>
                      </div>
                      <p className="text-[10px] text-muted-foreground">Upload a new {editSource.source_type === 'csv' ? 'CSV' : 'Excel'} file to replace the existing data.</p>
                      <input
                        type="file"
                        accept={editSource.source_type === 'csv' ? '.csv' : '.xlsx,.xls'}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 100 * 1024 * 1024) {
                            toast.error('File exceeds 100MB limit');
                            return;
                          }
                          if (editSource.source_type === 'excel') {
                            const reader = new FileReader();
                            reader.onload = () => setReuploadContent(reader.result.split(',')[1]);
                            reader.readAsDataURL(file);
                          } else {
                            const reader = new FileReader();
                            reader.onload = () => setReuploadContent(reader.result);
                            reader.readAsText(file);
                          }
                        }}
                        className="w-full text-xs text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:bg-primary/20 file:text-primary file:cursor-pointer hover:file:bg-primary/30"
                        data-testid="reupload-file-input"
                      />
                      {reuploadContent && (
                        <Button onClick={handleReupload} disabled={reuploading} className="w-full rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs active:scale-95" data-testid="reupload-btn">
                          {reuploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                          Replace Data with New File
                        </Button>
                      )}
                    </div>
                  </>
                )}

                {/* Save */}
                <div className="pt-2">
                  <Button onClick={handleEditSave} disabled={editSaving || !editName.trim()} className="w-full rounded-lg bg-primary text-primary-foreground text-xs active:scale-95" data-testid="edit-save-btn">
                    {editSaving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Pencil className="w-3.5 h-3.5 mr-1.5" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <DsUnsavedDialog />
    </div>
  );
}
